/**
 * ScriptureSidePanel
 * ═══════════════════════════════════════════════════════════════
 * Slide-in panel that displays the full text of a Scripture
 * citation plus AI-generated commentary.
 *
 * Verse lookup uses the existing `/api/classroom/voice-first/explain`
 * endpoint with a structured prompt requesting Hebrew text
 * (when the citation is from the Tanakh) + English translation +
 * brief contextual commentary.
 *
 * Cached per-citation in component state so re-opening the same
 * reference is instant after the first fetch.
 *
 * Usage:
 *   const [openRef, setOpenRef] = useState(null);
 *   <button onClick={() => setOpenRef("Exodus 3:14")}>Exodus 3:14</button>
 *   <ScriptureSidePanel
 *     reference={openRef}
 *     onClose={() => setOpenRef(null)}
 *     gradeLevel="Adult"
 *   />
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Module-level cache so re-opening the same verse across different
// pill clicks doesn't re-hit the LLM.
const verseCache = {};

export default function ScriptureSidePanel({ reference, onClose, gradeLevel = "Adult" }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const lastReqRef = useRef(null);

  useEffect(() => {
    if (!reference) {
      setData(null);
      setError(null);
      return;
    }
    if (verseCache[reference]) {
      setData(verseCache[reference]);
      setError(null);
      return;
    }
    const reqId = `${reference}-${Date.now()}`;
    lastReqRef.current = reqId;
    setLoading(true);
    setError(null);
    setData(null);

    const fetchVerse = async () => {
      const token = localStorage.getItem("token");
      const prompt =
        `Provide ONLY the following for the scriptural reference "${reference}", ` +
        `using clear headings:\n\n` +
        `HEBREW (if the reference is from the Tanakh / Old Testament — otherwise write "N/A"):\n` +
        `[Hebrew text]\n\n` +
        `ENGLISH:\n` +
        `[Literal English translation, preferably from a Hebrew-aware translation]\n\n` +
        `CONTEXT:\n` +
        `[2-3 sentences explaining the literary, historical, and covenantal context of this verse.]\n\n` +
        `COMMENTARY:\n` +
        `[3-4 sentences of commentary appropriate for a ${gradeLevel}-level learner, ` +
        `prioritizing the Hebrew / restoration-faith reading over Greco-Roman tradition.]`;
      try {
        const res = await axios.post(
          `${API}/classroom/voice-first/explain`,
          {
            topic: prompt,
            grade_level: gradeLevel,
          },
          { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 }
        );
        if (lastReqRef.current !== reqId) return; // user navigated away
        const rawText =
          res.data?.explanation_text ||
          res.data?.explanation ||
          res.data?.expanded_text ||
          res.data?.content ||
          "";
        // Defensive: ensure we hand parseSections a string.
        const text =
          typeof rawText === "string" ? rawText : JSON.stringify(rawText);
        const parsed = parseSections(text);
        const finalData = { reference, ...parsed, raw: text };
        verseCache[reference] = finalData;
        setData(finalData);
      } catch (e) {
        if (lastReqRef.current !== reqId) return;
        // Coerce to a string — FastAPI 422 responses return
        // `detail` as an ARRAY of objects, which React cannot
        // render directly (causes Minified React error #31 and
        // crashes the entire Special Edition section).
        const raw = e?.response?.data?.detail || e?.message || "Couldn't load this reference";
        const safeError =
          typeof raw === "string"
            ? raw
            : Array.isArray(raw)
              ? raw.map((d) => (d && typeof d === "object" ? (d.msg || JSON.stringify(d)) : String(d))).join("; ")
              : JSON.stringify(raw);
        setError(safeError);
      } finally {
        if (lastReqRef.current === reqId) setLoading(false);
      }
    };
    fetchVerse();
  }, [reference, gradeLevel]);

  // Close on Esc.
  useEffect(() => {
    if (!reference) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [reference, onClose]);

  if (!reference) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        data-testid="scripture-panel-backdrop"
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.55)",
          backdropFilter: "blur(2px)",
          zIndex: 9998,
        }}
      />
      {/* Panel */}
      <aside
        data-testid="scripture-side-panel"
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: "min(480px, 92vw)",
          background: "#0d0a14",
          borderLeft: "2px solid #DDA0DD",
          boxShadow: "-10px 0 30px rgba(0,0,0,0.5)",
          zIndex: 9999,
          padding: "24px",
          overflowY: "auto",
          color: "#eee",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "16px",
          }}
        >
          <h3 style={{ color: "#DDA0DD", margin: 0 }}>📖 {reference}</h3>
          <button
            onClick={onClose}
            data-testid="scripture-panel-close"
            aria-label="Close scripture panel"
            style={{
              background: "transparent",
              border: "1px solid #444",
              color: "#bbb",
              borderRadius: "6px",
              padding: "4px 10px",
              cursor: "pointer",
              fontSize: "0.9rem",
            }}
          >
            ✕ Close
          </button>
        </div>

        {loading && (
          <div style={{ color: "#888", padding: "20px 0", textAlign: "center" }}>
            Looking up {reference}…
          </div>
        )}
        {error && (
          <div
            style={{
              background: "#3a1a1a",
              border: "1px solid #ff6b6b",
              padding: "12px",
              borderRadius: "8px",
              color: "#ff9999",
              fontSize: "0.9rem",
            }}
          >
            {error}
          </div>
        )}
        {data && (
          <div style={{ fontSize: "0.92rem", lineHeight: "1.65" }}>
            {data.hebrew && data.hebrew.toUpperCase() !== "N/A" && (
              <Section title="HEBREW" color="#FFD700">
                <div
                  dir="rtl"
                  lang="he"
                  style={{ fontFamily: "Georgia, serif", fontSize: "1.15rem", lineHeight: 2 }}
                >
                  {data.hebrew}
                </div>
              </Section>
            )}
            {data.english && (
              <Section title="ENGLISH" color="#87CEEB">
                <div style={{ fontStyle: "italic", color: "#ddd" }}>{data.english}</div>
              </Section>
            )}
            {data.context && (
              <Section title="CONTEXT" color="#90EE90">
                <div>{data.context}</div>
              </Section>
            )}
            {data.commentary && (
              <Section title="COMMENTARY" color="#DDA0DD">
                <div>{data.commentary}</div>
              </Section>
            )}
            {/* If parsing produced nothing useful, fall back to the raw text */}
            {!data.hebrew && !data.english && !data.context && !data.commentary && (
              <div style={{ whiteSpace: "pre-wrap", color: "#ccc" }}>{data.raw}</div>
            )}
          </div>
        )}
      </aside>
    </>
  );
}

function Section({ title, color, children }) {
  return (
    <div style={{ marginBottom: "18px" }}>
      <div
        style={{
          color,
          fontSize: "0.75rem",
          letterSpacing: "0.06em",
          fontWeight: "bold",
          marginBottom: "6px",
        }}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

// Parse the AI response into our four labelled sections. The prompt
// asks for HEBREW / ENGLISH / CONTEXT / COMMENTARY headings — tolerate
// minor variations in formatting.
function parseSections(text) {
  if (!text) return {};
  const result = { hebrew: "", english: "", context: "", commentary: "" };
  const labels = ["HEBREW", "ENGLISH", "CONTEXT", "COMMENTARY"];
  const positions = [];
  for (const label of labels) {
    const re = new RegExp(`(^|\\n)\\s*\\*{0,2}${label}\\*{0,2}\\s*:?\\s*\\n?`, "i");
    const m = re.exec(text);
    if (m) positions.push({ label: label.toLowerCase(), idx: m.index + m[0].length, start: m.index });
  }
  positions.sort((a, b) => a.idx - b.idx);
  for (let i = 0; i < positions.length; i++) {
    const cur = positions[i];
    const next = positions[i + 1];
    const slice = text.substring(cur.idx, next ? next.start : text.length).trim();
    result[cur.label] = slice;
  }
  return result;
}

/**
 * Auto-linkify Scripture citations within a string. Returns an array
 * of React-renderable parts (strings + objects describing pills).
 *
 * Recognized patterns:
 *   "Exodus 3:14"
 *   "Isaiah 7:14"
 *   "Deuteronomy 6:4"
 *   "1 Samuel 13:14"
 *   "Genesis 2:7"
 *   "Numbers 27"
 *   "Matthew 1:23"
 *   "Mark 12:29"
 *   "Luke 2:21"
 *   "Hebrews 1:1-2"
 *   "Acts 1-2"
 *   "Psalm 82:6"
 *   "Hosea 11:1"
 *   "Jeremiah 31:9"
 */
const BIBLE_BOOKS = [
  "Genesis", "Exodus", "Leviticus", "Numbers", "Deuteronomy",
  "Joshua", "Judges", "Ruth", "1 Samuel", "2 Samuel", "1 Kings", "2 Kings",
  "1 Chronicles", "2 Chronicles", "Ezra", "Nehemiah", "Esther",
  "Job", "Psalm", "Psalms", "Proverbs", "Ecclesiastes",
  "Song of Solomon", "Isaiah", "Jeremiah", "Lamentations",
  "Ezekiel", "Daniel", "Hosea", "Joel", "Amos", "Obadiah",
  "Jonah", "Micah", "Nahum", "Habakkuk", "Zephaniah", "Haggai",
  "Zechariah", "Malachi",
  "Matthew", "Mark", "Luke", "John", "Acts",
  "Romans", "1 Corinthians", "2 Corinthians", "Galatians",
  "Ephesians", "Philippians", "Colossians", "1 Thessalonians",
  "2 Thessalonians", "1 Timothy", "2 Timothy", "Titus", "Philemon",
  "Hebrews", "James", "1 Peter", "2 Peter", "1 John", "2 John",
  "3 John", "Jude", "Revelation",
];
// Sort longest-first so "1 Samuel" matches before "Samuel".
const BOOK_PATTERN = BIBLE_BOOKS.sort((a, b) => b.length - a.length).join("|");
// Allow chapter[:verse[-endVerse]], or chapter-rangeChapter.
const CITATION_RE = new RegExp(
  `\\b(${BOOK_PATTERN})\\s+(\\d+(?:[-:]\\d+)*(?::\\d+(?:-\\d+)?)?)`,
  "g"
);

export function linkifyScripture(text, onTapRef) {
  // Defensive coercion: callers occasionally pass non-strings (e.g.,
  // raw object from a misshaped AI response, or `null` while a fetch
  // is in flight). Render path must never receive a raw object —
  // that triggers Minified React error #31 ("Objects are not valid
  // as a React child") and crashes the parent SectionErrorBoundary.
  if (text === null || text === undefined) return [""];
  if (typeof text !== "string") {
    try {
      text = JSON.stringify(text);
    } catch {
      return [""];
    }
  }
  const parts = [];
  let lastIdx = 0;
  let match;
  let key = 0;
  CITATION_RE.lastIndex = 0;
  while ((match = CITATION_RE.exec(text)) !== null) {
    const fullRef = `${match[1]} ${match[2]}`;
    if (match.index > lastIdx) {
      parts.push(text.substring(lastIdx, match.index));
    }
    parts.push(
      <button
        key={`scripref-${key++}-${match.index}`}
        type="button"
        onClick={() => onTapRef(fullRef)}
        data-testid={`scripture-pill-${fullRef.replace(/[\s:]/g, "-")}`}
        style={{
          background: "linear-gradient(135deg, #2a1a3a 0%, #4a2a5a 100%)",
          color: "#DDA0DD",
          border: "1px solid #DDA0DD",
          padding: "1px 8px",
          borderRadius: "6px",
          fontSize: "inherit",
          fontFamily: "inherit",
          fontWeight: "600",
          margin: "0 2px",
          cursor: "pointer",
          textDecoration: "none",
          display: "inline-block",
          lineHeight: "1.4",
        }}
        title={`Tap to read ${fullRef}`}
      >
        {fullRef}
      </button>
    );
    lastIdx = CITATION_RE.lastIndex;
  }
  if (lastIdx < text.length) {
    parts.push(text.substring(lastIdx));
  }
  return parts;
}
